import React from "react";
import { useSelector } from "react-redux";
import { UserTypeEnum } from "common/enums/UserType.enum";
import type { SellerPublicCard } from "reducers/companyProfileApi";
import { useGetSellerPublicCardQuery } from "reducers/companyProfileApi";
import { RootState } from "store/store";
import ProductSellerCompanySummary from "./ProductSellerCompanySummary";
import ProductSellerMetricPanel from "./ProductSellerMetricPanel";

const buildProfilePath = (
  userType: UserTypeEnum | undefined,
  companyCode: string,
) => {
  const sellerProfileQuery = `tab=profile&companyCode=${encodeURIComponent(
    companyCode,
  )}`;

  if (userType === UserTypeEnum.COMPANY_BUYER) {
    return `/buyer/information?${sellerProfileQuery}`;
  }

  if (userType === UserTypeEnum.COMPANY_SELLER) {
    return `/seller/information?${sellerProfileQuery}`;
  }

  return "/admin/company-management";
};

const SellerCardContent = ({
  card,
  profilePath,
}: {
  card: SellerPublicCard;
  profilePath?: string;
}) => (
  <div className="grid min-w-0 gap-5 pt-[15px] md:gap-[30px] md:pt-[30px] xl:grid-cols-2">
    <ProductSellerCompanySummary card={card} profilePath={profilePath} />
    <ProductSellerMetricPanel metrics={card.metrics} />
  </div>
);

interface ProductSellerCompanyCardProps {
  companyCode?: string;
  enableProfileLink?: boolean;
}

const ProductSellerCompanyCard = ({
  companyCode,
  enableProfileLink = false,
}: ProductSellerCompanyCardProps) => {
  const { userData } = useSelector((state: RootState) => state.auth);
  const { data, isError, isLoading } = useGetSellerPublicCardQuery(
    { companyCode: companyCode ?? "" },
    { skip: !companyCode },
  );
  const profilePath =
    enableProfileLink && companyCode
      ? buildProfilePath(userData?.type, companyCode)
      : undefined;

  return (
    <section className="w-full min-w-0 border-2 border-primary bg-white px-[18px] pb-[18px] pt-2 md:px-[30px] md:pb-[30px] md:pt-[20px]">
      <div className="flex w-full items-stretch gap-2.5 border-b border-[#d5d5d5] py-2 sm:gap-[14px] sm:py-[10px]">
        <div className="w-[4px] rounded-[2px] bg-primary" />
        <h2 className="text-[18px] font-bold leading-[160%] text-primary md:text-[22px]">
          売り手企業情報
        </h2>
      </div>

      {isLoading || !companyCode ? (
        <div className="flex min-h-[300px] items-center justify-center text-[15px] text-[#64748b]">
          読み込み中...
        </div>
      ) : isError || !data ? (
        <div className="flex min-h-[300px] items-center justify-center text-[15px] text-[#b42318]">
          売り手企業情報を取得できませんでした
        </div>
      ) : (
        <SellerCardContent card={data} profilePath={profilePath} />
      )}
    </section>
  );
};

export default ProductSellerCompanyCard;
